/**
 * API Service Layer
 * All API calls use centralized configuration from apiConfig.js
 * Refactored for consistency and maintainability
 */

import axiosInstance from '../config/axiosInstance';
import { buildApiUrl, API_BASE_URL } from '../config/apiConfig';

// Helper to get auth headers (for non-axios calls)
const getToken = () => localStorage.getItem('token');

const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ==================== TOPICS ====================

export const getTopics = async () => {
  const response = await axiosInstance.get('/topics');
  return response.data;
};

export const createTopic = async (topicData) => {
  const response = await axiosInstance.post('/admin/topics', topicData);
  return response.data;
};

export const updateTopic = async (topicId, topicData) => {
  const response = await axiosInstance.put(`/admin/topics/${topicId}`, topicData);
  return response.data;
};

export const deleteTopic = async (topicId) => {
  const response = await axiosInstance.delete(`/admin/topics/${topicId}`);
  return response.data;
};

export const seedTopics = async () => {
  const response = await axiosInstance.post('/admin/seed-topics', {});
  return response.data;
};

// ==================== QUESTIONS ====================

export const getQuestions = async (topicId = null, sort = 'newest', skip = 0) => {
  const params = new URLSearchParams({ sort, skip, limit: 50 });
  if (topicId) params.append('topic_id', topicId);
  
  const response = await axiosInstance.get(`/questions?${params.toString()}`);
  return response.data;
};

export const getQuestion = async (questionId) => {
  const response = await axiosInstance.get(`/questions/${questionId}`);
  return response.data;
};

export const createQuestion = async (questionData) => {
  const response = await axiosInstance.post('/questions', questionData);
  return response.data;
};

export const updateQuestion = async (questionId, questionData) => {
  const response = await axiosInstance.put(`/questions/${questionId}`, questionData);
  return response.data;
};

export const deleteQuestion = async (questionId) => {
  const response = await axiosInstance.delete(`/questions/${questionId}`);
  return response.data;
};

export const likeQuestion = async (questionId, action) => {
  const response = await axiosInstance.post(`/questions/${questionId}/like`, { action });
  return response.data;
};

export const getRelatedQuestions = async (questionId, limit = 5) => {
  const response = await axiosInstance.get(`/questions/${questionId}/related`, {
    params: { limit }
  });
  return response.data;
};

// Get question by slug
export const getQuestionBySlug = async (slug) => {
  const response = await axiosInstance.get(`/questions/by-slug/${slug}`);
  return response.data;
};

// View Tracking
export const trackView = async (questionId) => {
  const response = await axiosInstance.post(`/questions/${questionId}/view`, {});
  return response.data;
};

// ==================== ANSWERS ====================

export const getAnswers = async (questionId) => {
  const response = await axiosInstance.get(`/questions/${questionId}/answers`);
  return response.data;
};

export const createAnswer = async (answerData) => {
  const response = await axiosInstance.post('/answers', answerData);
  return response.data;
};

export const updateAnswer = async (answerId, answerData) => {
  const response = await axiosInstance.put(`/answers/${answerId}`, answerData);
  return response.data;
};

export const deleteAnswer = async (answerId) => {
  const response = await axiosInstance.delete(`/answers/${answerId}`);
  return response.data;
};

export const likeAnswer = async (answerId, action) => {
  const response = await axiosInstance.post(`/answers/${answerId}/like`, { action });
  return response.data;
};

// Mark answer as accepted (Admin)
export const markAnswerAccepted = async (answerId) => {
  const response = await axiosInstance.post(`/admin/answers/${answerId}/accept`, {});
  return response.data;
};

// Unmark answer as accepted (Admin)
export const unmarkAnswerAccepted = async (answerId) => {
  const response = await axiosInstance.post(`/admin/answers/${answerId}/unaccept`, {});
  return response.data;
};

// ==================== SEARCH ====================

export const searchQuestions = async (query) => {
  const response = await axiosInstance.get(`/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

export const advancedSearch = async (query, topicId = null, sortBy = 'relevance', limit = 20) => {
  const params = new URLSearchParams({ q: query, sort_by: sortBy, limit });
  if (topicId) params.append('topic_id', topicId);
  
  const response = await axiosInstance.get(`/search/advanced?${params.toString()}`);
  return response.data;
};

// ==================== USER ====================

export const getUserProfile = async (userId) => {
  const response = await axiosInstance.get(`/users/${userId}`);
  return response.data;
};

export const updateProfile = async (profileData) => {
  const response = await axiosInstance.put('/users/profile', profileData);
  return response.data;
};

// ==================== NOTIFICATIONS ====================

export const getNotifications = async (unreadOnly = false, limit = 50) => {
  const params = new URLSearchParams({ limit });
  if (unreadOnly) params.append('unread_only', 'true');
  
  const response = await axiosInstance.get(`/notifications?${params.toString()}`);
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await axiosInstance.get('/notifications/unread-count');
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await axiosInstance.post(`/notifications/${notificationId}/read`, {});
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await axiosInstance.post('/notifications/mark-all-read', {});
  return response.data;
};
// ==================== ANALYTICS ====================

export const getAnalytics = async () => {
  const response = await axiosInstance.get('/admin/analytics');
  return response.data;
};

export const getAdminStats = async () => {
  const response = await axiosInstance.get('/admin/analytics');
  return response.data;
};

// ==================== MODERATION & REPORTING ====================

export const submitReport = async (reportData) => {
  const response = await axiosInstance.post('/reports', reportData);
  return response.data;
};

export const getReports = async (status = null, contentType = null, limit = 50, skip = 0) => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (contentType) params.append('content_type', contentType);
  params.append('limit', limit.toString());
  params.append('skip', skip.toString());
  
  const response = await axiosInstance.get(`/admin/reports?${params.toString()}`);
  return response.data;
};

export const updateReportStatus = async (reportId, statusData) => {
  const response = await axiosInstance.put(`/admin/reports/${reportId}/status`, statusData);
  return response.data;
};

export const getReportStats = async () => {
  const response = await axiosInstance.get('/admin/reports/stats');
  return response.data;
};

// ==================== FILE UPLOAD ====================

// Image Upload (special handling for multipart/form-data)
export const uploadImage = async (formData) => {
  const response = await axiosInstance.post('/upload/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// ==================== ADMIN MANAGEMENT ====================

// Admin Question Management
export const getAdminQuestions = async (page = 1, limit = 20, search = null, topicId = null) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append('search', search);
  if (topicId) params.append('topic_id', topicId);
  
  const response = await axiosInstance.get(`/admin/questions?${params.toString()}`);
  return response.data;
};

export const updateAdminQuestion = async (questionId, questionData) => {
  const response = await axiosInstance.put(`/admin/questions/${questionId}`, questionData);
  return response.data;
};

export const deleteAdminQuestion = async (questionId) => {
  const response = await axiosInstance.delete(`/admin/questions/${questionId}`);
  return response.data;
};

// Bulk question actions
export const bulkQuestionAction = async (actionData) => {
  const response = await axiosInstance.post('/admin/questions/bulk', actionData);
  return response.data;
};

// Admin Answer Management
export const getAdminAnswers = async (page = 1, limit = 20, questionId = null) => {
  const params = new URLSearchParams({ page, limit });
  if (questionId) params.append('question_id', questionId);
  
  const response = await axiosInstance.get(`/admin/answers?${params.toString()}`);
  return response.data;
};

export const updateAdminAnswer = async (answerId, content) => {
  const response = await axiosInstance.put(`/admin/answers/${answerId}`, { content });
  return response.data;
};

export const deleteAdminAnswer = async (answerId) => {
  const response = await axiosInstance.delete(`/admin/answers/${answerId}`);
  return response.data;
};

// Admin User Management
export const getAdminUsers = async (page = 1, limit = 20, search = null) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.append('search', search);
  
  const response = await axiosInstance.get(`/admin/users?${params.toString()}`);
  return response.data;
};

export const banUser = async (userId, reason) => {
  const response = await axiosInstance.post(`/admin/users/${userId}/ban`, { reason });
  return response.data;
};

export const unbanUser = async (userId) => {
  const response = await axiosInstance.post(`/admin/users/${userId}/unban`, {});
  return response.data;
};

// ==================== AUTH ====================

// Password Reset
export const requestPasswordReset = async (email, captchaToken = null) => {
  const response = await axiosInstance.post('/auth/forgot-password', { 
    email,
    captcha_token: captchaToken
  });
  return response.data;
};

export const resetPassword = async (token, new_password) => {
  const response = await axiosInstance.post('/auth/reset-password', {
    token,
    new_password
  });
  return response.data;
};

// ==================== TOPIC FOLLOWING ====================

export const followTopic = async (topicId) => {
  const response = await axiosInstance.post(`/topics/${topicId}/follow`, {});
  return response.data;
};

export const unfollowTopic = async (topicId) => {
  const response = await axiosInstance.delete(`/topics/${topicId}/follow`);
  return response.data;
};

export const getTopicFollowStatus = async (topicId) => {
  const response = await axiosInstance.get(`/topics/${topicId}/follow-status`);
  return response.data;
};

export const getTopicStats = async (topicId) => {
  const response = await axiosInstance.get(`/topics/${topicId}/stats`);
  return response.data;
};

export const getTopContributors = async (topicId, limit = 5) => {
  const response = await axiosInstance.get(`/topics/${topicId}/contributors?limit=${limit}`);
  return response.data;
};

// Export API_BASE_URL for components that need direct fetch calls
export { API_BASE_URL, buildApiUrl };